import {Button, CircularProgress, Stack, TextField, Typography} from "@mui/material";
import React from "react";
import {useAuthState} from "react-firebase-hooks/auth";
import {useObject} from "react-firebase-hooks/database";
import {useDispatch} from "react-redux";
import type {IndustryUnits} from "../../firebase/index.js";
import {
	  addIndustry,
	  auth,
	  createCurrentIndustryHookQuery,
	  getIndustry,
	  getIndustryKeyFromName,
	  getIndustryNameFromKey,
	  getIndustryNamesArray,
	  Industry,
	  setCurrentIndustryKey
} from "../../firebase/index.js";
import {setInMemoryIndustry} from "../../redux/InMemoryIndustryReducer.js";
import {APXAutoComplete} from "../../reusables/APXAutoComplete.jsx";
import IndustryLayout from "./IndustryLayout.jsx";


export default function IndustryMainFragment() {
	  
	  const uid = useAuthState(auth)[0]?.uid;
	  
	  const [showSetup, setShowSetup] = React.useState(false);
	  
	  const [snapshot, loading, error] = useObject(createCurrentIndustryHookQuery(uid));
	  
	  const dispatch = useDispatch();
	  
	  React.useEffect(() => {
			 if (/*TRUTHY->*/ !!snapshot && snapshot.exists()) {
					const industryKey = snapshot.val();
					getIndustry(uid, industryKey, (industry) => {
						  if (/*TRUTHY->*/ !!industry) {
								 setShowSetup(false);
								 dispatch(setInMemoryIndustry({industry}));
						  } else {
								 setShowSetup(true);
						  }
					});
			 } else {
					!loading && setShowSetup(true);
			 }
	  }, [snapshot, loading]);
	  
	  
	  return (
				 <>
						{loading ?
								  <Stack spacing={2} padding={2} alignItems={"center"} justifyContent={"center"} sx={{height: "90vh"}}>
										 <CircularProgress/>
								  </Stack>
								  : showSetup ?
											 <IndustrySetup uid={uid} onSetupCompleted={() => setShowSetup(false)}/>
											 :
											 <IndustryLayout onIndustryChange={() => {
													dispatch(setInMemoryIndustry({industry: null}));
													setCurrentIndustryKey(uid, null,
															  () => {
															  }
													);
											 }}/>
						}
				 </>
	  );
}


function IndustrySetup({uid, onSetupCompleted}: { uid: string, onSetupCompleted: () => void }) {
	  const [chosenName, setChosenName] = React.useState(getIndustryNamesArray()[0]);
	  const [showUnitSetup, setShowUnitSetup] = React.useState(false);
	  const dispatch = useDispatch();
	  return (
				 <>
						{/*FALSY->*/ !!!showUnitSetup ?
								  <Stack spacing={2} padding={2} alignItems={"center"} justifyContent={"center"} sx={{height: "90vh"}}>
										 <APXAutoComplete options={getIndustryNamesArray()} value={chosenName} onOptionSelected={setChosenName} textFieldLabel={"Choose Industry"}/>
										 <Button variant="contained" sx={{m: 1, py: 1}}
													onClick={() => {
														  const industryKey = getIndustryKeyFromName(chosenName);
														  // FIRST, SET THE CURRENT INDUSTRY KEY IN FIREBASE
														  setCurrentIndustryKey(uid, industryKey, () => {
																 // THEN, CHECK IF THE INDUSTRY IS SET UP/ALREADY EXISTS OR NOT IN FIREBASE
																 // IF EXISTS, IT MEANS THE UNITS ARE ALREADY SET UP. SO, JUST SET THE IN-MEMORY INDUSTRY.
																 // ELSE, SHOW THE SETUP UNITS COMPONENT.
																 getIndustry(uid, industryKey, (industry) => {
																		if (/*TRUTHY->*/ !!industry) {
																			  dispatch(setInMemoryIndustry({industry}));
																			  onSetupCompleted();
																		} else {
																			  setShowUnitSetup(true);
																		}
																 });
														  });
													}}
										 >
												Set Current Industry
										 </Button>
								  </Stack>
								  :
								  <UnitSetup uid={uid}
												 industryKey={getIndustryKeyFromName(chosenName)}
												 onIndustryAdded={(industry) => {
														dispatch(setInMemoryIndustry({industry}));
														onSetupCompleted();
												 }}
								  />
						}
				 </>
	  );
}


function UnitSetup({uid, industryKey, onIndustryAdded}: { uid: string, industryKey: string, onIndustryAdded: (industry: Industry) => void }) {
	  const info = <p>
			 ⓘ This app is a helpful companion for budding entrepreneurs. So, these units are for your reference purpose only.
			 This app is <i><u>not</u></i> meant for any kind of unit-conversion.
			 Whatever industry-specific units you input, choose carefully because you will <i><u>not</u></i> be able to change later.
			 *<i><u>The first unit is required.</u></i>
	  </p>;
	  
	  const [units, setUnits] = React.useState(["", "", ""]);
	  const [disableButton, setDisableButton] = React.useState(true);
	  
	  React.useEffect(() => {
			 setDisableButton(/*FALSY->*/ !!!units[0]);
	  }, [units[0]]);
	  
	  return (
				 <>
						<Stack spacing={2} padding={2} alignItems={"center"} justifyContent={"center"} sx={{height: "90vh"}}>
							  <Typography variant={"h4"} color={"primary"}>This industry is not setup yet. Let's setup the industry units first.</Typography>
							  <Typography variant={"caption"} color={"rgba(255,255,255,0.75)"}>{info}</Typography>
							  <Stack direction={{xs: "column", sm: "row"}} spacing={2} justifyContent={"center"} alignItems={"center"}>
									 <TextField label={"Unit 1 Name"} variant={"outlined"} fullWidth required
													value={units[0]}
													onChange={(e) => setUnits([e.target.value, units[1], units[2]])}
									 />
									 <TextField label={"Unit 2 Name"} variant={"outlined"} fullWidth
													value={units[1]}
													onChange={(e) => setUnits([units[0], e.target.value, units[2]])}
									 />
									 <TextField label={"Unit 3 Name"} variant={"outlined"} fullWidth
													value={units[2]}
													onChange={(e) => setUnits([units[0], units[1], e.target.value])}
									 />
							  </Stack>
							  <Button disabled={disableButton} variant={"contained"} sx={{m: 1, py: 1}}
										 onClick={() => {
												const industryUnits: IndustryUnits = {unit1: units[0].trim(), unit2: units[1]?.trim(), unit3: units[2]?.trim()};
												const industryName = getIndustryNameFromKey(industryKey);
												const industry = new Industry(industryKey, industryName, industryUnits);
												addIndustry(uid, industryKey, industry, () => onIndustryAdded(industry));
										 }}
							  >
									 Setup Units
							  </Button>
						</Stack>
				 </>
	  );
}
